import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, inputCls } from "../admin/AFields";
import { pickLocalized } from "../../lib/localized";

export default function StudioFacebook() {
  const [settings, setSettings] = useState(null);
  const [greetings, setGreetings] = useState([]);
  const [form, setForm] = useState({ greeting_id: "", caption: "", hashtags: "", link: "" });
  const [busy, setBusy] = useState(false);
  const [postId, setPostId] = useState("");

  useEffect(() => {
    api.get("/studio/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/greetings/list?status=published").then((r) => setGreetings(r.data)).catch(() => {});
  }, []);

  const pick = (id) => {
    const g = greetings.find((x) => x.id === id);
    setForm({ greeting_id: id, caption: pickLocalized(g?.fb_caption) || pickLocalized(g?.message) || "", hashtags: (g?.hashtags || []).join(" "), link: "" });
    setPostId("");
  };

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/studio/facebook/publish", {
        greeting_id: form.greeting_id, caption: form.caption,
        hashtags: form.hashtags.split(/\s+/).filter(Boolean), link: form.link,
      });
      setPostId(r.data.post_id || "");
      toast.success("Published to Facebook Page");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const greeting = greetings.find((g) => g.id === form.greeting_id);

  return (
    <div data-testid="studio-facebook">
      <SEO title="Facebook Publishing" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Facebook Page Publishing</h1>
      <p className="mt-1 text-sm text-slate-500">Publish greetings directly to your Facebook Page via the Meta Graph API. Posts go live immediately — review carefully.</p>

      {settings && !settings.fb_configured && (
        <div className="mt-5 flex items-center gap-3 rounded-lg border border-saffron-500/40 bg-saffron-100/60 px-5 py-4" data-testid="fb-not-configured">
          <AlertTriangle size={18} className="shrink-0 text-saffron-600" />
          <p className="text-sm font-semibold text-navy">Facebook NOT CONFIGURED — add your Page ID and Page Access Token in <Link to="/studio/integrations" className="font-bold text-saffron-600 underline" data-testid="goto-integrations-fb">Studio → Integrations</Link>.</p>
        </div>
      )}

      <div className="mt-8 grid max-w-4xl gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Greeting (published)</label>
          <select value={form.greeting_id} onChange={(e) => pick(e.target.value)} className={inputCls} data-testid="fb-greeting">
            <option value="">Select greeting…</option>
            {greetings.map((g) => <option key={g.id} value={g.id}>{pickLocalized(g.title)} · {g.date}</option>)}
          </select>
          <div className="mt-5 space-y-5">
            <AreaF label="Caption" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} rows={4} ai contentType="greeting" />
            <TextF label="Hashtags (space separated)" value={form.hashtags} onChange={(v) => setForm({ ...form, hashtags: v })} />
            <TextF label="Link (optional)" value={form.link} onChange={(v) => setForm({ ...form, link: v })} placeholder="https://www.sahajsarathi.com/greetings/…" />
          </div>
          <button onClick={publish} disabled={busy || !form.greeting_id || !settings?.fb_configured} className="btn-primary mt-6 !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="fb-publish-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Facebook size={15} />} Publish to Facebook
          </button>
          {postId && (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-brandgreen-100 px-4 py-2.5 text-xs font-bold text-brandgreen" data-testid="fb-post-id">
              <CheckCircle2 size={14} /> Published — post ID: {postId}
            </p>
          )}
        </div>
        {greeting && (
          <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="fb-preview">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Preview</p>
            {greeting.image && <img src={greeting.image} alt="" className="mt-3 aspect-square w-full rounded-lg object-cover" />}
            <p className="mt-4 text-sm font-bold text-navy">{pickLocalized(greeting.title)}</p>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{form.caption}</p>
            {form.hashtags && <p className="mt-2 text-sm font-semibold text-[#1877F2]">{form.hashtags.split(/\s+/).filter(Boolean).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
